import type { Command, CommandContext } from "./Command";
import { findItem } from "../../inventory/Inventory";
import { withComponent } from "../../world/Entity";
import type { EntityId } from "../../world/Entity";
import type { ArtifactComponent, EntityComponent } from "../../world/components";

function artifactOf(components: readonly EntityComponent[] | undefined): ArtifactComponent | undefined {
  return components?.find((c): c is ArtifactComponent => c.type === "artifact");
}

/** Reveals an undiscovered artifact's true identity and unlocks the knowledge it teaches. */
export class DiscoverArtifactCommand implements Command {
  constructor(private readonly entityId: EntityId) {}

  execute(context: CommandContext): void {
    const entity = context.world.getEntity(this.entityId);
    if (entity) {
      const artifactComponent = entity.components.find((c) => c.type === "artifact");
      if (!artifactComponent) throw new Error(`Entity "${this.entityId}" is not an artifact`);

      const definition = context.artifacts.get(artifactComponent.definitionId);
      const revealedId = definition?.revealsAs ?? artifactComponent.definitionId;
      const revealed = context.artifacts.get(revealedId) ?? definition;
      if (!revealed) throw new Error(`Unknown artifact definition "${artifactComponent.definitionId}"`);

      context.world.updateEntity(this.entityId, (e) => ({
        ...withComponent(
          withComponent(e, "artifact", (a) => ({ ...a, definitionId: revealed.id, discovered: true })),
          "renderable",
          (r) => ({ ...r, asset: revealed.asset }),
        ),
        definitionId: revealed.id,
      }));

      for (const nodeId of revealed.knowledgeNodes) {
        context.knowledge.unlock(nodeId, "understood");
        context.events.emit("knowledge:unlocked", { nodeId, state: "understood" });
      }
      context.events.emit("artifact:discovered", { entityId: this.entityId, artifactId: revealed.id });
      return;
    }

    const item = findItem(context.world.inventory, this.entityId);
    if (!item) throw new Error(`Unknown entity "${this.entityId}"`);
    const components = (item.metadata as { components?: readonly EntityComponent[] } | undefined)?.components;
    const artifactComponent = artifactOf(components);
    if (!artifactComponent) throw new Error(`"${this.entityId}" is not an artifact`);

    const definition = context.artifacts.get(artifactComponent.definitionId);
    const revealedId = definition?.revealsAs ?? artifactComponent.definitionId;
    const revealed = context.artifacts.get(revealedId) ?? definition;
    if (!revealed) throw new Error(`Unknown artifact definition "${artifactComponent.definitionId}"`);

    context.world.updateInventoryItem(this.entityId, (i) => ({
      ...i,
      definitionId: revealed.id,
      metadata: {
        ...i.metadata,
        components: (components ?? []).map((c) =>
          c.type === "artifact" ? { ...c, definitionId: revealed.id, discovered: true } : c,
        ),
      },
    }));

    for (const nodeId of revealed.knowledgeNodes) {
      context.knowledge.unlock(nodeId, "understood");
      context.events.emit("knowledge:unlocked", { nodeId, state: "understood" });
    }
    context.events.emit("artifact:discovered", { entityId: this.entityId, artifactId: revealed.id });
  }
}
